import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Skeleton } from 'primereact/skeleton';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import { Accordion, AccordionTab } from 'primereact/accordion';
import useCartHandler from '../../utils/HandleCart';
import { TransferBasket, GetBasketData, GetCheckOutData } from "../../api/styleJewelleryService";
import { CartDelete, CartDeleteAll } from '../../api/cartService';
import { InsertWishlist } from '../../api/wishlistService';
import { useAuth } from '../../context/AuthContext';
import { useCount } from '../../context/CountContext';
import CustomerSelection from '../../components/Common/CustomerSelection';
import { HandelQuntityIncDec } from './StyleInventoryDetail';
import "../../css/StyleJewellery/style_cart.css";

const DEFAULT_IMAGE = "/assets/images/VaultAccess/no-image3.png";

const CartItemSkeleton = () => (
	<div className="style-cart-page">
		<motion.div className="cart-item-exact">
		<div className="item-left-visual" style={{ position: 'relative' }}>
			<div className="item-img-checkbox">
				<Skeleton borderRadius="4px" width="100%" height="100%" />
			</div>
			<div style={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				<Skeleton width="190px" height="190px" borderRadius="12px" />
			</div>
		</div>
		<div className="item-details-container">
			<div className="item-info-col">
				<h2 className="item-title-exact">
					<Skeleton width="85%" height="1.8rem" />
				</h2>
				<div className="item-ref-code">
					<Skeleton width="60%" height="0.9rem" />
				</div>
				<div className="item-ref-code">
					<Skeleton width="55%" height="0.9rem" />
				</div>
				<div className="item-clock-date" style={{ marginTop: 'auto' }}>
					<Skeleton width="140px" height="0.8rem" />
				</div>
			</div>

			<div className="spec-column">
				<div className="spec-title-exact"><Skeleton width="70%" height="1.1rem" /></div>
				<div className="spec-row-exact">
					<span className="spec-label-exact"><Skeleton width="60px" height="0.8rem" /></span>
					<span className="spec-value-exact"><Skeleton width="40px" height="0.8rem" /></span>
				</div>
				<div className="spec-row-exact">
					<span className="spec-label-exact"><Skeleton width="60px" height="0.8rem" /></span>
					<span className="spec-value-exact"><Skeleton width="50px" height="0.8rem" /></span>
				</div>
				<div className="spec-row-exact">
					<span className="spec-label-exact"><Skeleton width="60px" height="0.8rem" /></span>
					<span className="spec-value-exact"><Skeleton width="35px" height="0.8rem" /></span>
				</div>
			</div>

			<div className="spec-column">
				<div className="spec-title-exact"><Skeleton width="70%" height="1.1rem" /></div>
				<div className="spec-row-exact">
					<span className="spec-label-exact"><Skeleton width="60px" height="0.8rem" /></span>
					<span className="spec-value-exact"><Skeleton width="55px" height="0.8rem" /></span>
				</div>
				<div className="spec-row-exact">
					<span className="spec-label-exact"><Skeleton width="60px" height="0.8rem" /></span>
					<span className="spec-value-exact"><Skeleton width="45px" height="0.8rem" /></span>
				</div>
				<div className="spec-row-exact">
					<span className="spec-label-exact"><Skeleton width="60px" height="0.8rem" /></span>
					<span className="spec-value-exact"><Skeleton width="40px" height="0.8rem" /></span>
				</div>
			</div>

			<div className="item-actions-col">
				<div className="item-price-exact fs-4">
					<Skeleton width="120px" height="2.2rem" />
				</div>
				<div className="count_inc_dec d-flex align-items-center rounded px-2 py-1" style={{ width: '130px', height: '40px', background: '#f1f5f9', border: '1px solid #e2e8f0' }}>
					<Skeleton width="100%" height="20px" />
				</div>
				<div className="item-action-btns-wrapper" style={{ marginTop: 'auto' }}>
					<Skeleton shape="circle" size="32px" />
				</div>
			</div>
		</div>
	</motion.div>
	</div>
);

const CartList = () => {

	document.title = "Style Cart | Meera Jewellery House Admin";
	const navigate = useNavigate();
	const { user, showToast } = useAuth();
	const {
		fetchStyleCartCount,
		fetchWishlistCount,
		setEnquiryItems,
		setIsEnquiryOpen,
		submitEnquiry: submitEnquiryGlobal
	} = useCount();
	const { HandleCartInsert, HandleCartRemove } = useCartHandler();
	const { setCartCount, setWishlistCount } = useCount();
	const [subtotal, setSubtotal] = useState(0);
	const [total, setTotal] = useState(0);
	const [cartData, setCartData] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [dataFetched, setDataFetched] = useState(false);
	const [selectedIds, setSelectedIds] = useState([]);

	const groupedData = useMemo(() => {
		const groups = {};
		cartData.forEach(item => {
			const customerName = item.CustomerName || "Unknown Customer";
			if (!groups[customerName]) {
				groups[customerName] = [];
			}
			groups[customerName].push(item);
		});
		return groups;
	}, [cartData]);

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [itemToDelete, setItemToDelete] = useState(null);
	const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

	const fetchCartData = async () => {
		try {
			setIsLoading(true);
			const requestParams = {
				Party_ID: user?.SelectedCustomer_ID || "0",
				StockCategory: "STYLE",
				BasketType: "ORDER"
			};
			const response = await GetBasketData(requestParams);
			const dataToSet = response?.ROW || [];

			setCartData(dataToSet);
			setDataFetched(true);
		} catch (error) {
			console.error("Error fetching cart data:", error);
			showToast({
				title: "Error",
				message: "Failed to fetch cart data.",
				type: "error"
			});
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchCartData();
	}, [user?.SelectedCustomer_ID]);

	const handleSelectAll = (e) => {
		if (e.target.checked) {
			setSelectedIds(cartData.map(item => item.Cart_ID));
		} else {
			setSelectedIds([]);
		}
	};

	const handleSelectItem = (id) => {
		setSelectedIds(prev =>
			prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
		);
	};

	const openDeleteModal = (item) => {
		setItemToDelete(item);
		setShowDeleteModal(true);
	};

	const confirmDelete = async () => {
		if (!itemToDelete) return;

		if (!user?.SelectedCustomer_ID || user?.SelectedCustomer_ID === 0) {
			showToast({
				title: "Selection Required",
				message: "Please select a specific customer to remove items.",
				type: "warning"
			});
			return;
		}

		try {
			const res = await CartDelete({
				Party_ID: user?.SelectedCustomer_ID,
				Style_ID: itemToDelete.Style_ID,
				Cart_ID: itemToDelete.Cart_ID?.toString()
			});
			if (res?.ZMESSAGE?.[0]?.RETURNCODE === "1" || res?.Message?.[0]?.RETURNCODE === "1") {
				fetchStyleCartCount(user?.SelectedCustomer_ID);
				fetchCartData();
				setShowDeleteModal(false);
				setItemToDelete(null);
				showToast({
					title: "Removed",
					message: "Item removed from cart.",
					type: "success"
				});
			}
		} catch (error) {
			console.error("Delete failed:", error);
		}
	};

	const handleMoveToWishlist = async () => {
		if (!itemToDelete) return;
		try {
			const res = await InsertWishlist({
				Party_ID: user?.SelectedCustomer_ID,
				Style_ID: itemToDelete.Style_ID,
				Product_Category: itemToDelete.Category || "STYLE"
			});
			
			if (res?.ZMESSAGE?.[0]?.RETURNCODE === "1" || res?.Message?.[0]?.RETURNCODE === "1") {
				// After adding to Wishlist, remove from cart
				await confirmDelete();
				fetchWishlistCount(user?.SelectedCustomer_ID);
				showToast({
					title: "Moved to Wishlist",
					message: "Item has been successfully moved to your Wishlist.",
					type: "success"
				});
			} else {
				showToast({
					title: "Info",
					message: res?.ZMESSAGE?.[0]?.MESSAGE || "Item might already be in Wishlist.",
					type: "info"
				});
			}
		} catch (error) {
			console.error("Move to Wishlist failed:", error);
			showToast({
				title: "Error",
				message: "Failed to move item to Wishlist.",
				type: "error"
			});
		}
	};

	const openBulkDeleteModal = () => {
		if (selectedIds.length > 0) {
			setShowBulkDeleteModal(true);
		}
	};

	const confirmBulkDelete = async () => {
		if (!user?.SelectedCustomer_ID || user?.SelectedCustomer_ID === 0) {
			showToast({
				title: "Selection Required",
				message: "Please select a specific customer to remove items.",
				type: "warning"
			});
			return;
		}

		try {
			const selectedItems = cartData.filter(item => selectedIds.includes(item.Cart_ID));
			const items = selectedItems.map(item => ({
				Cart_ID: item.Cart_ID?.toString(),
				Style_ID: item.Style_ID?.toString()
			}));

			const res = await CartDeleteAll({
				Party_ID: user?.SelectedCustomer_ID,
				Items: items
			});

			if (res?.ZMESSAGE?.[0]?.RETURNCODE === "1" || res?.Message?.[0]?.RETURNCODE === "1") {
				fetchStyleCartCount(user?.SelectedCustomer_ID);
				fetchCartData();
				setShowBulkDeleteModal(false);
				setSelectedIds([]);
				showToast({
					title: "Removed",
					message: "Selected items removed from cart.",
					type: "success"
				});
			}
		} catch (error) {
			console.error("Bulk delete failed:", error);
		}
	};

	const handleProceedToEnquiry = async () => {
		const selectedItems = cartData.filter(item => selectedIds.includes(item.Cart_ID));
		if (selectedItems.length === 0) return;

		const enquiryItemsFormatted = selectedItems.map(item => ({
			Style_ID: item.Style_ID,
			StyleNo: item.StyleNo,
			JewelCode: item.JewelCode,
			Shortdescription: item.Line1 || item.StyleNo,
			MetalQuality: item.MetalQuality,
			MetalTone: item.MetalTone,
			TotalNetWt: item.TotalNetWt ? `${item.TotalNetWt} g` : "-",
			Cart_ID: item.Cart_ID?.toString()
		}));

		setEnquiryItems(enquiryItemsFormatted);
		setIsEnquiryOpen(true);
	};

	const formatDate = (dateString) => {
		if (!dateString) return "-";
		const date = new Date(dateString);
		return date.toLocaleDateString('en-IN', {
			day: '2-digit',
			month: 'short',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	
	const handleSetlectedItems = async () => {
		const selectedItems = cartData.filter(item => selectedIds.includes(item.Cart_ID));
		if (!selectedItems || selectedItems.length === 0) {
			showToast({
				type: "warning",
				message: "Please select product to proceed with checkout",
			});
			return;
		}
		try {
			const selectedDataIds = selectedItems.map((item) => item.Cart_ID);
			const items = selectedDataIds.map((id) => ({
				Basket_ID: id.toString()
			}));

			const checkoutResponse = await GetCheckOutData({
				Party_ID: user?.SelectedCustomer_ID || "0",
				Items: items
			});

			const checkoutData = checkoutResponse?.ROW || [];
			const checkoutSummary = checkoutResponse?.ROW1?.[0] || {};

			navigate("/Checkout", { 
				state: { 
					selectedItems, 
					checkoutData,
					checkoutSummary
				} 
			});
		} catch (error) {
			console.error("Error in handleSetlectedItems :", error);
		}
	};

	const formatPrice = (price) => {
		let numericPrice = 0;
		if (typeof price === "number") {
			numericPrice = price;
		} else if (typeof price === "string") {
			numericPrice = parseFloat(price.replace(/[^\d.]/g, ""));
		}

		return new Intl.NumberFormat('en-IN', {
			style: 'currency',
			currency: 'INR',
			maximumFractionDigits: 0
		}).format(numericPrice || 0).replace('₹', 'Rs. ');
	};

	const calculateSelectedTotal = () => {
		const itemsToSum = selectedIds.length === 0 ? cartData : cartData.filter(item => selectedIds.includes(item.Cart_ID));
		return itemsToSum.reduce((acc, item) => {
			let price = 0;
			const rawValue = item.TotalAmount;

			if (rawValue !== null && rawValue !== undefined) {
				if (typeof rawValue === 'number') {
					price = rawValue;
				} else {
					// Remove everything except digits and decimal point
					const cleanValue = rawValue.toString().replace(/[^\d.]/g, "");
					price = parseFloat(cleanValue);
				}
			}

			return acc + (isNaN(price) ? 0 : price);
		}, 0);
	};

	// #region TOTAL PRICE AND QUANTITY
	const [itemsTotalPrice, setItemsTotalPrice] = useState([]);

	const handleUpdateTotalPrice = (itemId, newTotalPrice) => {
		setItemsTotalPrice(prevState => {
			let previousPrice = null;

			const updatedState = prevState.map(item => {
				if (item.Cart_ID === itemId) {
					previousPrice = item.TotalAmount;
					return { ...item, price: newTotalPrice };
				}
				return item;
			});

			if (previousPrice !== null) {
				const priceDifference = newTotalPrice - previousPrice;
				setTotal(prevTotal => prevTotal + priceDifference);
				setSubtotal(prevSubtotal => prevSubtotal + priceDifference);
			}

			return updatedState;
		});
	};

	// #region UPDATE QUANTITY

	const parseLineAmount = (val) => {
		if (!val) return 0;
		return parseFloat(String(val).replace(/Rs\.?\s*|,/g, '')) || 0;
	};

	const handleUpdateQuantityInCart = (itemId, quantity, styleId) => {
		const safeQty = Math.max(1, quantity);

		const selectedProduct = cartData.find(item => item.Style_ID === styleId);
		if (!selectedProduct) return;

		const currentTotal = parseLineAmount(selectedProduct.LineAmount);
		const currentQty = Number(selectedProduct.Qty) || 1;
		const unitPrice = currentTotal / currentQty;
		const newRowTotal = unitPrice * safeQty;

		const updatedCart = cartData.map(item => item.Style_ID === styleId ? { ...item, Qty: safeQty, LineAmount: formatPrice(newRowTotal), TotalAmount: newRowTotal } : item );
		setCartData(updatedCart);

		// Removed redundant subtotal/total updates. calculateSelectedTotal() handles this in render.

		const qtyChange = safeQty - selectedProduct.Qty;
		if (qtyChange !== 0) {
			handleCartBasketInsert(selectedProduct, qtyChange, false, unitPrice);
		}
	};
	// #endregion UPDATE QUANTITY

	// #endregion TOTAL PRICE AND QUANTITY

	const itemVariants = {
		hidden: { opacity: 0, y: 30 },
		show: {
			opacity: 1,
			y: 0,
			transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }
		},
		exit: {
			opacity: 0,
			y: 20,
			transition: { duration: 0.2 }
		}
	};

	const deleteModalFooter = (
		<div className="cart-modal-footer dual-actions">
			<button className="cart-modal-btn-secondary" onClick={handleMoveToWishlist}>
				<i className="pi pi-heart" style={{ marginRight: '8px' }} />
				Move to Wishlist
			</button>
			<button className="cart-modal-btn-danger" onClick={confirmDelete}>
				<i className="pi pi-trash" style={{ marginRight: '8px' }} />
				Remove Item
			</button>
		</div>
	);

	const bulkDeleteModalFooter = (
		<div className="cart-modal-footer">
			<button className="cart-modal-btn-secondary" onClick={() => setShowBulkDeleteModal(false)}>Cancel</button>
			<button className="cart-modal-btn-danger" onClick={confirmBulkDelete}>
				<i className="pi pi-trash" style={{ marginRight: '8px' }} />
				Remove {selectedIds.length} Items
			</button>
		</div>
	);

	const renderCartItem = (item, index) => (
		<motion.div
			key={item.Cart_ID || index}
			variants={itemVariants}
			layout
			className={`cart-item-exact ${selectedIds.includes(item.Cart_ID) ? 'selected' : ''}`}
		>
			<div className="item-left-visual">
				<input
					type="checkbox"
					className="item-img-checkbox"
					checked={selectedIds.includes(item.Cart_ID)}
					onChange={() => handleSelectItem(item.Cart_ID)}
				/>
				<Link to={`/detail?style_ID=${item.Style_ID}`} style={{ display: 'block', height: '100%' }}>
					<img
						src={item.Image1 || DEFAULT_IMAGE}
						alt={item.Line1}
						onError={(e) => { e.target.src = DEFAULT_IMAGE; }}
					/>
				</Link>
			</div>

			<div className="item-details-container">
				<div className="item-info-col">
					<h2 className="item-title-exact">{item.Line1 || "Untitled Design"}</h2>
					<div className="item-ref-code">
						Jewel Code: <span>{item.JewelCode}</span>
					</div>
					<div className="item-ref-code">
						Style No: <span>{item.StyleNo}</span>
					</div>
					<div className="item-clock-date">
						<i className="pi pi-clock" style={{ fontSize: '0.9rem' }} />
						{formatDate(item.InsertDatetime || item.CartEntryDate)}
					</div>
				</div>

				<div className="spec-column">
					<div className="spec-title-exact">Product Details</div>
					<div className="spec-row-exact">
						<span className="spec-label-exact">Purity</span>
						<span className="spec-value-exact">{item.MetalQuality || "-"}</span>
					</div>
					<div className="spec-row-exact">
						<span className="spec-label-exact">Gold Wt</span>
						<span className="spec-value-exact">{item.TotalNetWt ? `${item.TotalNetWt} g` : "-"}</span>
					</div>
					<div className="spec-row-exact">
						<span className="spec-label-exact">Dimension</span>
						<span className="spec-value-exact">{item.ItemSize || "-"}</span>
					</div>
				</div>

				<div className="spec-column">
					<div className="spec-title-exact">
						{item.CDiaWt ? "Stone Specs" : "Diamond Details"}
					</div>
					<div className="spec-row-exact">
						<span className="spec-label-exact">Weight</span>
						<span className="spec-value-exact">
							{item.DiaPcs || 0} Pcs / {item.DiaWt || 0} Cts
						</span>
					</div>
					<div className="spec-row-exact">
						<span className="spec-label-exact">Gross Wt</span>
						<span className="spec-value-exact">{item.GrossWt ? `${item.GrossWt} g` : "-"}</span>
					</div>
					<div className="spec-row-exact">
						<span className="spec-label-exact">Color</span>
						<span className="spec-value-exact">{item.MetalTone || "-"}</span>
					</div>
				</div>

				<div className="item-actions-col">
					<div className="item-price-exact fs-4">
						{item.LineAmount}
					</div>
					<div className="count_inc_dec d-flex align-items-center rounded px-2 py-1">
						<HandelQuntityIncDec
							setTotalPrice={handleUpdateTotalPrice}
							unitPrice={item.LineAmount}
							qty={item.Qty}
							itemId={item.Cart_ID}
							styleId={item.Style_ID}
							updateQuantityInCart={handleUpdateQuantityInCart}
						/>
					</div>
					<div className="item-action-btns-wrapper" style={{ marginTop: 'auto' }}>
						<button
							className="delete-btn-exact"
							onClick={() => openDeleteModal(item)}
							title="Remove Item"
						>
							<i className="pi pi-trash" />
						</button>
					</div>
				</div>
			</div>
		</motion.div>
	);

	// #region ADD TO CART - QUANTITY
	const handleCartBasketInsert = async (selectedProduct, quantity, shouldFetchFullCart, unitPrice) => {
		// Ensure rate is a clean numeric string for backend decimal parsing
		const cleanRate = unitPrice 
			? String(unitPrice) 
			: String(parseLineAmount(selectedProduct.LineAmount) / (Number(selectedProduct.Qty) || 1));

		const { success } = await HandleCartInsert({
			request: {
				Style_ID: selectedProduct.Style_ID,
				JewelCode: selectedProduct.JewelCode,
				StyleNo: selectedProduct.StyleNo,
				Quantity: String(quantity),
				StockCategory: "",
				Rate: cleanRate,
			},
			fetchData: shouldFetchFullCart ? fetchCartData : null
		});
		if (success) {
			fetchStyleCartCount(setCartCount, null);
			fetchWishlistCount(setWishlistCount, null)
		}
		else {
			fetchStyleCartCount(setCartCount, null);
			fetchWishlistCount(setWishlistCount, null)
		};
	};
	// #endregion ADD TO CART

	return (
		<div className="style-cart-page">
			<div className="cart-header-card">
				<div className="cart-header-top-row">
					<h1 className="cart-page-title">
						Style Cart <span>({cartData.length} items)</span>
					</h1>
					<div className="cart-header-actions-right">
						<div className="header-select-all">
							<span>SELECT ALL ({selectedIds.length.toString().padStart(1, '0')})</span>
							{selectedIds.length > 0 && (
								<button
									className="header-delete-selected-btn"
									onClick={openBulkDeleteModal}
									title="Remove Selected Items"
								>
									<i className="pi pi-trash" />
								</button>
							)}
							<input
								type="checkbox"
								className="item-img-checkbox"
								checked={selectedIds.length > 0 && selectedIds.length === cartData.length}
								onChange={handleSelectAll}
								style={{ position: 'static' }}
							/>
						</div>
						<div className="header-actions-group">
							<div className="cart-header-actions-left">
								<CustomerSelection />
							</div>
							<button className="back-btn" onClick={() => navigate(-1)} aria-label="Go back">
								<i className="pi pi-arrow-left"></i>
							</button>
						</div>
					</div>
				</div>
			</div>

			{dataFetched && cartData.length === 0 ? (
				<div className="empty-cart-premium">
					<div className="empty-cart-visual">
						<i className="pi pi-shopping-bag" />
					</div>
					<div className="empty-cart-content">
						<h2 className="empty-cart-title">Your cart is Empty</h2>
						<p className="empty-cart-text">
							It looks like you haven't added any designs to your cart yet.
							Explore our collections to find your perfect match.
						</p>
						<Link to="/StyleInventory" className="btn-primary">
							<i className="pi pi-compass me-2" />
							Browse Collections
						</Link>
					</div>
				</div>
			) : (
				(Number(user?.SelectedCustomer_ID || 0) === 0) ? (
					<div className="accordion-full-width">
						<AnimatePresence>
							{isLoading ? (
								<div className="customer-items-list">
									{Array.from({ length: 3 }).map((_, i) => (
										<CartItemSkeleton key={i} />
									))}
								</div>
							) : (
								<Accordion multiple={true} className="customer-accordion">
									{Object.entries(groupedData).map(([customerName, items]) => (
										<AccordionTab
											key={customerName}
											header={
												<div className="accordion-header-content">
													<span>{customerName}</span>
													<span className="customer-badge">{items.length} items</span>
												</div>
											}
										>
											<div className="customer-items-list">
												{items.map((item, index) => renderCartItem(item, index))}
											</div>
										</AccordionTab>
									))}
								</Accordion>
							)}
						</AnimatePresence>
					</div>
				) : (
					<div className="cart-layout">
						<div className="cart-main-section">
							<div className="cart-items-list-exact">
								<AnimatePresence>
									{isLoading ? (
										<motion.div
											className="customer-items-list"
											key="skeleton-loader"
											initial={{ opacity: 0 }}
											animate={{ opacity: 1 }}
											exit={{ opacity: 0 }}
										>
											{Array.from({ length: 3 }).map((_, i) => (
												<CartItemSkeleton key={i} />
											))}
										</motion.div>
									) : (
										<motion.div
											key="cart-content"
											variants={{
												show: {
													transition: {
														staggerChildren: 0.1
													}
												}
											}}
											initial="hidden"
											animate="show"
										>
											<div className="customer-items-list">
												{cartData.map((item, index) => renderCartItem(item, index))}
											</div>
										</motion.div>
									)}
								</AnimatePresence>
							</div>
						</div>

						<aside className="summary-sidebar">
							<h2 className="summary-title">Cart Summary</h2>

							{isLoading ? (
								<>
									<div className="summary-row">
										<Skeleton width="100px" height="1rem" />
										<Skeleton width="30px" height="1rem" />
									</div>
									<div className="summary-row">
										<Skeleton width="80px" height="1rem" />
										<Skeleton width="60px" height="1rem" />
									</div>
									<div className="summary-row">
										<Skeleton width="120px" height="1rem" />
										<Skeleton width="90px" height="1rem" />
									</div>
									<div className="summary-row total" style={{ marginTop: '2rem' }}>
										<Skeleton width="120px" height="1.5rem" />
										<Skeleton width="100px" height="1.5rem" />
									</div>
									<Skeleton width="100%" height="50px" borderRadius="8px" style={{ marginTop: '2.5rem' }} />
								</>
							) : (
								<>
									<div className="summary-row">
										<span className="spec-label-exact">{selectedIds.length === 0 ? 'Total Items' : 'Selected Items'}</span>
										<span className="spec-value-exact">{selectedIds.length === 0 ? cartData.length : selectedIds.length}</span>
									</div>

									<div className="summary-row">
										<span className="spec-label-exact">Subtotal</span>
										<span className="spec-value-exact">{formatPrice(calculateSelectedTotal())}</span>
									</div>

									<div className="summary-row">
										<span className="spec-label-exact">Estimated Tax</span>
										<span className="spec-value-exact">Calculated at Checkout</span>
									</div>

									<div className="summary-row total">
										<span className="spec-label-exact">Total Amount</span>
										<span>{formatPrice(calculateSelectedTotal())}</span>
									</div>

									<div className="checkout-btn-wrapper">
										<button
											className="btn-primary w-100"
											onClick={handleSetlectedItems}
										>
											Proceed To Checkout
										</button>
									</div>
								</>
							)}


							<Link to="/StyleInventory" className="continue-shopping">
								Continue Shopping
								<i className="pi pi-arrow-right" style={{ fontSize: '0.8rem', marginLeft: '8px' }} />
							</Link>
						</aside>
					</div>
				)
			)}

			<Dialog
				visible={showDeleteModal}
				onHide={() => setShowDeleteModal(false)}
				footer={deleteModalFooter}
				draggable={false}
				resizable={false}
				showHeader={false}
				maskClassName="cart-modal-mask"
				className="cart-premium-modal"
				dismissableMask
			>
				<div className="cart-modal-content-wrapper">
					<button className="modal-close-x" onClick={() => setShowDeleteModal(false)}>
						<i className="pi pi-times" />
					</button>
					<div className="cart-modal-header-visual">
						<div className="cart-modal-icon-bg">
							<i className="pi pi-shopping-bag" />
						</div>
						<h2 className="cart-modal-title">Remove Item?</h2>
						<p className="cart-modal-subtitle">This item will be removed from your style cart.</p>
					</div>
					{itemToDelete && (
						<div className="cart-modal-product-preview">
							<div className="preview-image-container">
								<img
									src={itemToDelete.Image1 || DEFAULT_IMAGE}
									alt={itemToDelete.StyleNo}
									onError={(e) => { e.currentTarget.src = DEFAULT_IMAGE; }}
								/>
							</div>
							<div className="preview-details">
								<div className="preview-name">{itemToDelete.Line1 || "Untitled Design"}</div>
								<div className="preview-style">Style: {itemToDelete.StyleNo}</div>
								<div className="preview-code">Code: {itemToDelete.JewelCode}</div>
							</div>
						</div>
					)}
				</div>
			</Dialog>

			<Dialog
				visible={showBulkDeleteModal}
				onHide={() => setShowBulkDeleteModal(false)}
				footer={bulkDeleteModalFooter}
				draggable={false}
				resizable={false}
				showHeader={false}
				maskClassName="cart-modal-mask"
				className="cart-premium-modal"
				dismissableMask
			>
				<div className="cart-modal-content-wrapper">
					<button className="modal-close-x" onClick={() => setShowBulkDeleteModal(false)}>
						<i className="pi pi-times" />
					</button>
					<div className="cart-modal-header-visual">
						<div className="cart-modal-icon-bg" style={{ background: '#fef2f2', color: '#ef4444' }}>
							<i className="pi pi-trash" />
						</div>
						<h2 className="cart-modal-title">Remove Selected?</h2>
						<p className="cart-modal-subtitle">Are you sure you want to remove <b>{selectedIds.length} items</b> from your style cart?</p>
					</div>
					<div className="bulk-selection-preview-list">
						{cartData.filter(item => selectedIds.includes(item.Cart_ID)).slice(0, 3).map((item, i) => (
							<div key={i} className="cart-modal-product-preview mini">
								<div className="preview-image-container mini">
									<img src={item.Image1 || DEFAULT_IMAGE} alt={item.StyleNo} />
								</div>
								<div className="preview-details">
									<div className="preview-name mini">{item.Line1 || item.StyleNo}</div>
								</div>
							</div>
						))}
						{selectedIds.length > 3 && (
							<div className="more-items-label">+ {selectedIds.length - 3} more items</div>
						)}
					</div>
				</div>
			</Dialog>
		</div>
	);
};

export default CartList;